import { useMemo } from 'react';
import { 
  useGetResidentTransactionInfoQuery,
  useGetResidentPaymentsQuery 
} from '@/store/api/admin/residentListApi';
import { IAdminTransaction, ITransactionSummary } from '@/types/admin/transactions';

interface UseResidentTransactionsParams {
  accountId: number;
  leaseId: number;
  autoFetch?: boolean;
}

interface UseResidentTransactionsReturn {
  transactions: IAdminTransaction[];
  summary: ITransactionSummary;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

export const useResidentTransactions = ({
  accountId,
  leaseId,
  autoFetch = true,
}: UseResidentTransactionsParams): UseResidentTransactionsReturn => {
  
  // Fetch PMS transactions (charges from Yardi/Entrata)
  const { 
    data: transactionsData, 
    isLoading: isLoadingTransactions, 
    error: transactionsError,
    isFetching: isFetchingTransactions,
    refetch: refetchTransactions 
  } = useGetResidentTransactionInfoQuery(
    { accountId, leaseId },
    { skip: !autoFetch || !accountId || !leaseId }
  );

  // Fetch Resio payments
  const { 
    data: paymentsData, 
    isLoading: isLoadingPayments, 
    error: paymentsError,
    isFetching: isFetchingPayments,
    refetch: refetchPayments 
  } = useGetResidentPaymentsQuery(
    { accountId, leaseId },
    { skip: !autoFetch || !accountId || !leaseId }
  );

  // Merge and process all transactions
  const transactions = useMemo(() => {
    const pmsTransactions = transactionsData?.transactions || [];
    const resioPayments = paymentsData?.payments || [];

    // Filter out PMS transactions that have paymentId to avoid duplicates
    // (we already show those payments from the dedicated payments endpoint)
    const pmsChargesOnly = pmsTransactions.filter((tx: any) => !tx.paymentId);

    // Combine both sources
    const allTransactions = [
      ...pmsChargesOnly.map((tx: any) => ({
        transactionId: tx.transactionId || tx.id,
        transactionDate: tx.transactionDate,
        amount: typeof tx.amount === 'number' ? tx.amount : parseFloat(tx.amount || '0'),
        description: tx.description || 'Transaction',
        type: 'charge', // All PMS transactions without paymentId are charges
        arCode: tx.arCode,
        arCodeDescription: tx.arCodeDescription,
        paymentId: tx.paymentId,
        paidByFirstName: tx.paidByFirstName,
        paidByLastName: tx.paidByLastName,
        paidByUserId: tx.paidByUserId,
        paymentLedgerAmount: tx.paymentLedgerAmount,
        paymentConvenienceFee: tx.paymentConvenienceFee,
        paymentSettlementStatus: tx.paymentSettlementStatus,
        hasFundingData: tx.hasFundingData,
        lateFees: tx.lateFees,
        postMonth: tx.postMonth,
        amountPaid: tx.amountPaid || tx.amount,
      } as IAdminTransaction)),
      ...resioPayments, // Already formatted from backend
    ];

    // Sort by date (most recent first)
    return allTransactions.sort((a, b) => 
      new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
    );
  }, [transactionsData, paymentsData]);

  // Calculate summary statistics
  const summary = useMemo((): ITransactionSummary => {
    if (transactions.length === 0) {
      return {
        totalPayments: 0,
        totalCharges: 0,
        currentBalance: 0,
        transactionCount: 0,
      };
    }

    const totalPayments = transactions
      .filter((tx: IAdminTransaction) => tx.type === 'payment')
      .reduce((sum: number, tx: IAdminTransaction) => sum + Math.abs(tx.amount), 0);

    const totalCharges = transactions
      .filter((tx: IAdminTransaction) => tx.type === 'charge')
      .reduce((sum: number, tx: IAdminTransaction) => sum + Math.abs(tx.amount), 0);

    const currentBalance = totalCharges - totalPayments;

    const lastTransactionDate = transactions.reduce((latest, tx) => {
      if (!latest) return tx.transactionDate;
      return new Date(tx.transactionDate) > new Date(latest) ? tx.transactionDate : latest;
    }, '' as string);

    return {
      totalPayments,
      totalCharges,
      currentBalance,
      transactionCount: transactions.length,
      lastTransactionDate: lastTransactionDate || undefined,
    };
  }, [transactions]);

  // Refetch both endpoints
  const refetch = () => {
    refetchTransactions();
    refetchPayments();
  };

  return {
    transactions,
    summary,
    isLoading: isLoadingTransactions || isLoadingPayments || isFetchingTransactions || isFetchingPayments,
    error: transactionsError || paymentsError,
    refetch,
  };
};

